import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Box, 
  Typography,
  Alert,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Stack,
  Tooltip,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Autocomplete,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import { useTranslation } from 'react-i18next';
import CloseButton from './Common/CloseButton';
import ActionPathField from './Actions/ActionPathField';
import { apiRequest } from '../Utils/apiClient';

const TEMPLATE_IMAGE_MAX_DIMENSION = 256;
const TEMPLATE_IMAGE_MAX_BASE64_LENGTH = 4 * Math.ceil((1024 * 1024) / 3) + 64;
const TEMPLATE_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);

const HelpTip = ({ title }) => (
  <Tooltip title={title} arrow placement="top" enterDelay={350}>
    <IconButton
      size="small"
      aria-label={title}
      onClick={(event) => event.stopPropagation()}
      sx={{ p: 0.35, color: 'text.secondary', verticalAlign: 'middle' }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 17 }} />
    </IconButton>
  </Tooltip>
);

const TemplateSetupFieldPreview = ({ field, fieldLabel, question, helpText, example }) => {
  const { t } = useTranslation();
  const fieldType = String(field?.type || '').toLowerCase();
  const options = Array.isArray(field?.options) ? field.options : [];
  const fieldHelp = field?.helperText || field?.description
    ? t(field.helperText || field.description, { defaultValue: field.helperText || field.description })
    : '';
  const displayedHelp = [helpText, fieldHelp].filter(Boolean).join(' ');

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mt: 1, borderRadius: 2, bgcolor: 'rgba(87,87,176,.025)' }}>
      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {t('template.creator_field_preview', 'What the user will see')}
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {question || t('template.creator_preview_question_placeholder', 'Your question will appear here')}
      </Typography>
      {fieldType === 'path' && field.pathMode === 'output_file' ? (
        <ActionPathField
          label={fieldLabel}
          value=""
          onChange={() => {}}
          placeholder={field.placeholder || 'C:\\Output\\{filename}.{file_extension}'}
          disabled
          tags={[]}
          browseLabel={t('template.setup_choose_output_folder', 'Browse destination folders')}
          browseDisabled
          onBrowse={() => {}}
          helperText={displayedHelp || undefined}
        />
      ) : fieldType === 'file' || fieldType === 'folder' ? (
        <Button
          size="small"
          variant="outlined"
          disabled
          startIcon={fieldType === 'file' ? <InsertDriveFileRoundedIcon /> : <FolderOpenRoundedIcon />}
          sx={{ textTransform: 'none' }}
        >
          {fieldType === 'file'
            ? t('template.setup_choose_file', 'Choose a file')
            : t('template.setup_choose_folder', 'Choose a folder')}
        </Button>
      ) : fieldType === 'checkbox' ? (
        <FormControlLabel
          control={<Checkbox disabled checked={false} />}
          label={fieldLabel}
          sx={{ m: 0, alignItems: 'flex-start' }}
        />
      ) : (
        <TextField
          select={fieldType === 'select' && options.length > 0}
          fullWidth
          size="small"
          disabled
          label={fieldLabel}
          value=""
          type={['number', 'password', 'email', 'url', 'date', 'time', 'datetime-local'].includes(fieldType) ? fieldType : 'text'}
          multiline={fieldType === 'textarea'}
          minRows={fieldType === 'textarea' ? 2 : undefined}
          helperText={fieldType !== 'checkbox' ? displayedHelp || undefined : undefined}
        >
          {fieldType === 'select' && options.length > 0 && (
            <MenuItem value="">{t('template.creator_preview_choose', 'Choose an option')}</MenuItem>
          )}
          {options.map((option, index) => {
            const optionValue = option && typeof option === 'object' ? option.value : option;
            const optionLabel = option && typeof option === 'object' ? option.label ?? option.value : option;
            return (
              <MenuItem key={`${String(optionValue)}-${index}`} value={optionValue}>
                {t(optionLabel, { defaultValue: String(optionLabel ?? '') })}
              </MenuItem>
            );
          })}
        </TextField>
      )}
      {example && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.7 }}>
          {t('template.setup_example', 'Example')}: {example}
        </Typography>
      )}
      {['checkbox', 'file', 'folder'].includes(fieldType) && displayedHelp && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {displayedHelp}
        </Typography>
      )}
      <Button size="small" variant="contained" disabled sx={{ mt: 1, textTransform: 'none' }}>
        {t('template.setup_save_and_continue', 'Save and continue')}
      </Button>
    </Paper>
  );
};

const getSetupNodeKindLabel = (kind, t) => {
  if (kind === 'input') return t('template.creator_kind_input', 'Data source');
  if (kind === 'flow') return t('template.creator_kind_flow', 'Automated workflow');
  return t('template.creator_kind_action', 'Project step');
};

const getSetupNodeKindHelp = (kind, t) => {
  if (kind === 'input') return t('template.creator_kind_input_help', 'This is a data source already used by the project. The user may need to connect their own account or choose their own files.');
  if (kind === 'flow') return t('template.creator_kind_flow_help', 'This is an automated part of the project. Choose it when the user needs to review how or when it runs.');
  return t('template.creator_kind_action_help', 'This is a step that processes or delivers project data. Choose it when the user needs to adjust that step.');
};

const getSetupFieldPath = (field) => (typeof field === 'string' ? field : String(field?.path || ''));

const getFriendlySetupFieldLabel = (field, t, node) => {
  const path = getSetupFieldPath(field);
  if (field && typeof field === 'object' && field.label) {
    return t(field.label, { defaultValue: field.label });
  }
  if (node?.kind === 'action' && String(node.action_type || '').toLowerCase() === 'manualtag') {
    if (path === 'template') return t('template.creator_manual_tag_content', 'Tag content');
    if (path === 'outputTag') return t('template.creator_manual_tag_output', 'Name of the created tag');
  }
  return String(path || '')
    .split('.')
    .map((segment) => segment
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase()))
    .join(' › ');
};

export const moveRequirement = (requirements, index, direction) => {
  const targetIndex = index + direction;
  if (
    !Array.isArray(requirements)
    || index < 0
    || index >= requirements.length
    || targetIndex < 0
    || targetIndex >= requirements.length
  ) {
    return requirements;
  }

  const reordered = [...requirements];
  [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
  return reordered;
};

const resizeImageToDataUrl = (file) => new Promise((resolve, reject) => {
  if (!TEMPLATE_IMAGE_TYPES.has(file.type)) {
    reject(new Error('image_type'));
    return;
  }

  const reader = new FileReader();
  reader.onerror = () => reject(new Error('image_read'));
  reader.onload = () => {
    const image = new Image();
    image.onerror = () => reject(new Error('image_invalid'));
    image.onload = () => {
      const scale = Math.min(
        1,
        TEMPLATE_IMAGE_MAX_DIMENSION / image.naturalWidth,
        TEMPLATE_IMAGE_MAX_DIMENSION / image.naturalHeight,
      );
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));

      try {
        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const dataUrl = canvas.toDataURL(outputType, 0.85);
        if (dataUrl.length > TEMPLATE_IMAGE_MAX_BASE64_LENGTH) {
          reject(new Error('image_too_large'));
          return;
        }
        resolve(dataUrl);
      } catch {
        reject(new Error('image_invalid'));
      }
    };
    image.src = reader.result;
  };
  reader.readAsDataURL(file);
});

/**
 * SaveTemplateDialog Component
 * 
 * A dialog component for saving cases as templates.
 * Allows users to specify template name, description, and version number.
 * 
 * Features:
 * - Template name input (required)
 * - Template description input (optional)
 * - Version number input (defaults to 1)
 * - Form validation
 * - Keyboard support (Enter to submit)
 * - Internationalization support
 * - Consistent styling with the application theme
 */
const SaveTemplateDialog = ({
  open,
  onClose,
  onSave,
  caseData,
  primaryColor = '#5757b0',
  hoverColor = '#7777d0'
}) => {
  const { t } = useTranslation();
  
  // Form state
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [version, setVersion] = useState('1');
  const [templateImage, setTemplateImage] = useState('');
  const [imageError, setImageError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [setupNodes, setSetupNodes] = useState([]);
  const [setupNodesLoading, setSetupNodesLoading] = useState(false);
  const [setupNodesError, setSetupNodesError] = useState('');
  const [requirements, setRequirements] = useState([]);
  const [selectedNodeRef, setSelectedNodeRef] = useState('');
  const [selectedFieldPath, setSelectedFieldPath] = useState('');
  const [connectionStep, setConnectionStep] = useState(false);
  const [requirementQuestion, setRequirementQuestion] = useState('');
  const [requirementHelp, setRequirementHelp] = useState('');
  const [requirementExample, setRequirementExample] = useState('');
  const [requirementRequired, setRequirementRequired] = useState(true);
  const [confirmExisting, setConfirmExisting] = useState(false);
  const [editingRequirementId, setEditingRequirementId] = useState('');
  const [requirementQuestionAttempted, setRequirementQuestionAttempted] = useState(false);
  const [explanationOpen, setExplanationOpen] = useState(false);
  const setupNodesRequestId = useRef(0);

  const loadSetupNodes = useCallback(() => {
    if (!caseData?.id) {
      setSetupNodesLoading(false);
      return;
    }
    const requestId = setupNodesRequestId.current + 1;
    setupNodesRequestId.current = requestId;
    setSetupNodesError('');
    setSetupNodesLoading(true);
    apiRequest(`/api/v1/projects/${encodeURIComponent(caseData.id)}/template-setup-nodes`)
      .then((response) => {
        if (requestId === setupNodesRequestId.current) {
          setSetupNodes(Array.isArray(response?.nodes) ? response.nodes : []);
        }
      })
      .catch((error) => {
        if (requestId === setupNodesRequestId.current) {
          setSetupNodesError(error?.message || t('template.setup_nodes_error', 'Could not load parts of this project.'));
        }
      })
      .finally(() => {
        if (requestId === setupNodesRequestId.current) setSetupNodesLoading(false);
      });
  }, [caseData?.id, t]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      // Pre-fill with case title if available
      setTemplateName(caseData?.title ? `${caseData.title} Template` : '');
      setTemplateDescription(caseData?.description || '');
      setVersion('1');
      setTemplateImage('');
      setImageError('');
      setActiveStep(0);
      setRequirements([]);
      setSetupNodes([]);
      setSetupNodesError('');
      setSelectedNodeRef('');
      setSelectedFieldPath('');
      setConnectionStep(false);
      setRequirementQuestion('');
      setRequirementHelp('');
      setRequirementExample('');
      setRequirementRequired(true);
      setConfirmExisting(false);
      setEditingRequirementId('');
      setRequirementQuestionAttempted(false);
      setExplanationOpen(false);
      loadSetupNodes();
    } else {
      // Clear form when closing
      setTemplateName('');
      setTemplateDescription('');
      setVersion('1');
      setTemplateImage('');
      setImageError('');
      setActiveStep(0);
      setRequirements([]);
      setSetupNodes([]);
      setSetupNodesLoading(false);
      setSetupNodesError('');
      setSelectedNodeRef('');
      setSelectedFieldPath('');
      setConnectionStep(false);
      setRequirementQuestion('');
      setRequirementHelp('');
      setRequirementExample('');
      setRequirementRequired(true);
      setConfirmExisting(false);
      setEditingRequirementId('');
      setRequirementQuestionAttempted(false);
      setExplanationOpen(false);
    }
    return () => { setupNodesRequestId.current += 1; };
  }, [open, caseData, loadSetupNodes]);

  const setupNodeOptions = useMemo(() => {
    const existingInputCounts = {};
    setupNodes.forEach((node) => {
      if (node.kind !== 'input') return;
      const connectorType = String(node.input_type || node.ref.split(':')[1] || '').toLowerCase();
      existingInputCounts[connectorType] = (existingInputCounts[connectorType] || 0) + 1;
    });
    const existingInputIndexes = {};
    return setupNodes.map((node) => {
      if (node.kind !== 'input') {
        if (
          node.kind === 'action'
          && String(node.action_type || '').toLowerCase() === 'manualtag'
          && String(node.label || '').trim().toLowerCase() === 'manualtag'
        ) {
          return { ...node, label: t('manual_tag', 'Manual Tag') };
        }
        return node;
      }
      const connectorType = String(node.input_type || node.ref.split(':')[1] || '').toLowerCase();
      const ordinal = existingInputIndexes[connectorType] || 0;
      existingInputIndexes[connectorType] = ordinal + 1;
      const sourceName = String(node.source_name || '').trim();
      const sourceOrdinal = existingInputCounts[connectorType] > 1 ? ` (#${ordinal + 1})` : '';
      return {
        ...node,
        label: sourceName
          ? `${node.label} — ${sourceName}${sourceOrdinal}`
          : existingInputCounts[connectorType] > 1
            ? `${node.label} #${ordinal + 1}`
            : node.label,
      };
    });
  }, [setupNodes, t]);
  const selectedNode = setupNodeOptions.find((node) => node.ref === selectedNodeRef);
  const selectedField = (selectedNode?.fields || []).find(
    (field) => getSetupFieldPath(field) === selectedFieldPath
  );
  const duplicateFieldRequirement = Boolean(selectedFieldPath && requirements.some((requirement) => (
    requirement.id !== editingRequirementId
    && requirement.node_ref === selectedNode?.ref
    && requirement.field_path === selectedFieldPath
  )));
  const requirementDraftHasContent = Boolean(
    selectedNodeRef || connectionStep || requirementQuestion.trim() || requirementHelp.trim() || requirementExample.trim()
  );

  const resetRequirementDraft = () => {
    setSelectedNodeRef('');
    setSelectedFieldPath('');
    setConnectionStep(false);
    setRequirementQuestion('');
    setRequirementHelp('');
    setRequirementExample('');
    setRequirementRequired(true);
    setConfirmExisting(false);
    setEditingRequirementId('');
    setRequirementQuestionAttempted(false);
    setExplanationOpen(false);
  };

  const handleAddRequirement = () => {
    setRequirementQuestionAttempted(true);
    if (!selectedNode || !requirementQuestion.trim() || duplicateFieldRequirement) return;
    const nextRequirement = {
      id: editingRequirementId || `setup-${Date.now()}-${requirements.length}`,
      node_ref: selectedNode.ref,
      node_kind: selectedNode.kind,
      node_label: selectedNode.label,
      field_path: connectionStep ? '' : selectedFieldPath,
      field_schema: connectionStep ? undefined : (selectedNode.fields || []).find(
        (field) => getSetupFieldPath(field) === selectedFieldPath
      ) || undefined,
      question: requirementQuestion.trim(),
      help_text: requirementHelp.trim(),
      example: requirementExample.trim(),
      required: requirementRequired,
      confirm_existing: confirmExisting,
      ...(connectionStep ? { setup_kind: 'connection' } : {}),
    };
    setRequirements((current) => (editingRequirementId
      ? current.map((item) => (item.id === editingRequirementId ? nextRequirement : item))
      : [...current, nextRequirement]));
    resetRequirementDraft();
  };

  const handleEditRequirement = (requirement) => {
    const knownFields = setupNodeOptions.find((node) => node.ref === requirement.node_ref)?.fields || [];
    const isKnownField = knownFields.some((field) => getSetupFieldPath(field) === requirement.field_path);
    setEditingRequirementId(requirement.id);
    setSelectedNodeRef(requirement.node_ref);
    setSelectedFieldPath(requirement.field_path && isKnownField ? requirement.field_path : '');
    setConnectionStep(
      requirement.setup_kind === 'connection'
      || Boolean(
        setupNodeOptions.find((node) => node.ref === requirement.node_ref)?.authentication?.accountField
        && setupNodeOptions.find((node) => node.ref === requirement.node_ref)?.authentication?.accountField === requirement.field_path
      )
    );
    setRequirementQuestion(requirement.question || '');
    setRequirementHelp(requirement.help_text || '');
    setExplanationOpen(Boolean(requirement.help_text));
    setRequirementExample(requirement.example || '');
    setRequirementRequired(requirement.required !== false);
    setConfirmExisting(Boolean(requirement.confirm_existing));
    setRequirementQuestionAttempted(false);
  };

  const handleMoveRequirement = (index, direction) => {
    setRequirements((current) => moveRequirement(current, index, direction));
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setImageError('');
    try {
      setTemplateImage(await resizeImageToDataUrl(file));
    } catch (error) {
      const errorKey = error?.message;
      const message = errorKey === 'image_type'
        ? t('template.image_type_error') || 'Choose a PNG, JPEG, or WebP image.'
        : errorKey === 'image_too_large'
          ? t('template.image_size_error') || 'The image could not be reduced below the allowed size.'
          : t('template.image_invalid') || 'The selected image could not be read.';
      setTemplateImage('');
      setImageError(message);
    }
  };

  const handleRemoveImage = () => {
    setTemplateImage('');
    setImageError('');
  };

  const handleSubmit = () => {
    const trimmedName = templateName.trim();
    const trimmedDescription = templateDescription.trim();
    const versionNumber = parseInt(version) || 1;
    
    if (!trimmedName) {
      // Don't submit if name is empty
      return;
    }

    onSave({
      templateName: trimmedName,
      templateDescription: trimmedDescription,
      version: versionNumber,
      templateImage,
      setupRequirements: requirements,
      caseId: caseData?.id
    });
    
    handleClose();
  };

  const handleClose = () => {
    setTemplateName('');
    setTemplateDescription('');
    setVersion('1');
    setTemplateImage('');
    setImageError('');
    setActiveStep(0);
    setRequirements([]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 12px 40px rgba(87, 87, 176, 0.2)',
          overflow: 'hidden',
          position: 'relative',
          minHeight: '450px',
          maxHeight: '820px',
        }
      }}
    >
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${primaryColor}, ${hoverColor})`,
        color: 'white',
        fontWeight: '600',
        fontSize: '1.25rem',
        py: 2.5,
        px: { xs: 2, sm: 3 },
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '70px',
        boxSizing: 'border-box',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <SaveIcon sx={{ fontSize: '1.4rem', flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography component="div" variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {t('template.save_as_template') || 'Save as Template'}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,.86)', lineHeight: 1.25 }}>
              {t('template.creator_dialog_subtitle', 'Create a reusable starting point that others can understand.')}
            </Typography>
          </Box>
        </Box>
        <CloseButton
          onClick={handleClose}
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
            p: 0.8,
          }}
        />
      </DialogTitle>
      
      <DialogContent sx={{ 
        "--dialog-content-top-padding": "0px",
        pt: 0, 
        pb: 0, 
        px: { xs: 2, sm: 4 },
        mt: 0,
        minHeight: '360px',
        maxHeight: '620px',
        overflowY: 'auto',
        background: 'linear-gradient(180deg, rgba(247, 247, 255, 0.8) 0%, rgba(255, 255, 255, 1) 100%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        gap: 2,
      }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mt: 2, mb: 1 }}>
          {[
            {
              label: t('template.creator_details', 'Template details'),
              help: t('template.creator_details_help', 'Name your template and tell people what it is for.'),
            },
            {
              label: t('template.creator_setup', 'Setup instructions'),
              help: t('template.creator_setup_step_help', 'Add plain-language steps only where extra guidance is useful.'),
            },
            {
              label: t('template.creator_preview', 'Preview'),
              help: t('template.creator_preview_step_help', 'Check what someone will see before saving.'),
            },
          ].map(({ label, help }) => (
            <Step key={label}>
              <StepLabel optional={<Typography component="span" variant="caption" color="text.secondary">{help}</Typography>}>
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 ? <>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          label={t('template.name') || 'Template Name'}
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          required
          error={!templateName.trim() && templateName !== ''}
          helperText={!templateName.trim() && templateName !== ''
            ? t('template.creator_name_error', 'Enter a name to continue.')
            : t('template.creator_name_help', 'Choose a name people can recognize when they browse templates.')}
          sx={{
            mt: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              backgroundColor: 'white',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              minHeight: '56px',
              '& fieldset': {
                borderColor: 'rgba(87, 87, 176, 0.2)',
                borderWidth: '1.5px',
              },
              '&:hover fieldset': {
                borderColor: hoverColor,
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderColor: primaryColor,
                borderWidth: '2px',
                boxShadow: `0 0 0 3px rgba(87, 87, 176, 0.1)`,
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(87, 87, 176, 0.7)',
              fontWeight: '500',
              '&.Mui-focused': {
                color: primaryColor,
              },
            },
          }}
        />

        <TextField
          fullWidth
          variant="outlined"
          label={t('template.description') || 'Template Description'}
          value={templateDescription}
          onChange={(e) => setTemplateDescription(e.target.value)}
          multiline
          rows={3}
          placeholder={t('template.description_placeholder') || 'Describe what this template is for...'}
          helperText={t('template.creator_description_help', 'Explain what this project helps with and who it is for. This helps people choose the right starting point.')}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              backgroundColor: 'white',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              '& fieldset': {
                borderColor: 'rgba(87, 87, 176, 0.2)',
                borderWidth: '1.5px',
              },
              '&:hover fieldset': {
                borderColor: hoverColor,
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderColor: primaryColor,
                borderWidth: '2px',
                boxShadow: `0 0 0 3px rgba(87, 87, 176, 0.1)`,
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(87, 87, 176, 0.7)',
              fontWeight: '500',
              '&.Mui-focused': {
                color: primaryColor,
              },
            },
          }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            border: '1px dashed rgba(87, 87, 176, 0.35)',
            borderRadius: '10px',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
          }}
        >
          {templateImage ? (
            <Box
              component="img"
              src={templateImage}
              alt={t('template.image_alt') || 'Template preview'}
              sx={{
                width: 72,
                height: 72,
                objectFit: 'cover',
                borderRadius: '10px',
                border: '1px solid rgba(87, 87, 176, 0.2)',
              }}
            />
          ) : (
            <Box
              sx={{
                width: 72,
                height: 72,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '10px',
                color: 'rgba(87, 87, 176, 0.55)',
                backgroundColor: 'rgba(87, 87, 176, 0.06)',
              }}
            >
              <PhotoCameraIcon />
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ color: primaryColor, fontWeight: 600 }}>
              {t('template.image') || 'Template image'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {t('template.image_help') || 'Optional. It will be resized to fit within 256 × 256 pixels.'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Tooltip title={t('template.creator_image_help', 'An image is optional. It helps people recognize this template in the template list.')} arrow>
                <Button component="label" variant="outlined" size="small" startIcon={<PhotoCameraIcon />}>
                  {t('template.choose_image') || 'Choose image'}
                  <input
                    hidden
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleImageChange}
                  />
                </Button>
              </Tooltip>
              {templateImage && (
                <Button
                  variant="text"
                  size="small"
                  color="inherit"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={handleRemoveImage}
                >
                  {t('template.remove_image') || 'Remove'}
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {imageError && (
          <Alert severity="error" variant="outlined" sx={{ borderRadius: '10px' }}>
            {imageError}
          </Alert>
        )}

        <Alert
          severity="warning"
          variant="outlined"
          sx={{
            borderRadius: '10px',
            backgroundColor: 'rgba(255, 244, 229, 0.9)',
            borderColor: 'rgba(255, 183, 77, 0.6)',
            color: '#7d5b00',
            '& .MuiAlert-icon': {
              color: '#7d5b00',
            },
            fontSize: '0.95rem',
            lineHeight: 1.4,
          }}
        >
          {t('template.confidential_warning') || 'Remove all confidential data (paths, API keys, secrets, etc.) before sharing this template with external users.'}
        </Alert>

        <Accordion disableGutters variant="outlined" sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
            <Typography variant="body2" sx={{ fontWeight: 650 }}>{t('template.creator_advanced_details', 'Advanced details')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              fullWidth
              label={t('template.version') || 'Version'}
              type="number"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              inputProps={{ min: 1, step: 1 }}
              helperText={t('template.creator_version_help', 'Leave this at 1 unless you are publishing a deliberate update to an existing template.')}
            />
          </AccordionDetails>
        </Accordion>
        </> : activeStep === 1 ? <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
          <Box sx={{ p: 2, borderRadius: 2.5, background: 'linear-gradient(135deg, rgba(87,87,176,.09), rgba(87,87,176,.025))', border: '1px solid rgba(87,87,176,.15)' }}>
            <Typography variant="h6" sx={{ color: '#30306a', fontWeight: 750, mb: 0.45 }}>
              {t('template.creator_setup_heading', 'Help people get started')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
              {t('template.creator_setup_intro', 'Add plain-language steps for anything a new user must connect, choose, or review. Each step points to something already in this project.')}
            </Typography>
          </Box>
          <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
            {t('template.creator_setup_privacy', 'We show setting names only to help you write clear instructions. Existing values, account details, passwords, and secrets are never included in these instructions.')}
          </Alert>
          {setupNodesLoading && <Alert severity="info">{t('template.creator_loading_nodes', 'Finding the parts of this project…')}</Alert>}
          {setupNodesError && (
            <Alert
              severity="warning"
              action={(
                <Button color="inherit" size="small" onClick={loadSetupNodes}>
                  {t('template.retry', 'Retry')}
                </Button>
              )}
            >
              {setupNodesError}
            </Alert>
          )}
          {!setupNodesLoading && !setupNodesError && setupNodeOptions.length === 0 && (
            <Alert severity="info">{t('template.creator_no_nodes', 'There are no configurable project steps yet. Add the data source or step to the project first, then return here.')}</Alert>
          )}

          {requirements.length > 0 ? (
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 750, flex: 1 }}>
                  {t('template.creator_saved_questions', 'Instructions for new users')}
                </Typography>
                <Chip size="small" color="primary" variant="outlined" label={t('template.creator_step_count', '{{count}} steps', { count: requirements.length })} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
                {t('template.creator_reorder_help', 'Use the arrows to choose the order in which these steps will be shown.')}
              </Typography>
              {requirements.map((requirement, index) => (
                <Paper key={requirement.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2.25, borderColor: 'rgba(87,87,176,.18)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ width: 30, height: 30, flexShrink: 0, display: 'grid', placeItems: 'center', borderRadius: '50%', bgcolor: 'rgba(87,87,176,.1)', color: 'primary.main', fontWeight: 800 }}>
                      {index + 1}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.65, mb: 0.35 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 750 }}>{requirement.question}</Typography>
                        {requirement.setup_kind === 'connection' && (
                          <Chip size="small" color="info" variant="outlined" label={t('template.setup_connection', 'Connection')} />
                        )}
                        <Tooltip title={requirement.required
                          ? t('template.creator_required_help', 'This marks the step as important; it does not block the project from running.')
                          : t('template.setup_optional_help', 'This step is helpful but can be skipped.')}
                        arrow>
                          <Chip
                            size="small"
                            tabIndex={0}
                            variant="outlined"
                            label={requirement.required
                              ? t('template.creator_important_short', 'Important')
                              : t('template.setup_optional', 'Optional')}
                          />
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {requirement.node_kind === 'input' ? <StorageRoundedIcon sx={{ fontSize: 15 }} /> : requirement.node_kind === 'flow' ? <AccountTreeRoundedIcon sx={{ fontSize: 15 }} /> : <BoltRoundedIcon sx={{ fontSize: 15 }} />}
                        {requirement.node_label} · {getSetupNodeKindLabel(requirement.node_kind, t)}
                      </Typography>
                      {requirement.help_text && <Typography variant="body2" sx={{ mt: 0.65, whiteSpace: 'pre-wrap' }}>{requirement.help_text}</Typography>}
                      {requirement.field_path && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4 }}>{t('template.creator_field', 'Specific setting')}: {getFriendlySetupFieldLabel(requirement.field_schema || requirement.field_path, t)}</Typography>}
                      {requirement.example && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.45 }}>{t('template.creator_example_prefix', 'For example:')} {requirement.example}</Typography>}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Tooltip title={t('template.creator_move_question_up', 'Move this step up')} arrow>
                        <span>
                          <IconButton
                            aria-label={t('template.creator_move_question_up', 'Move this step up')}
                            onClick={() => handleMoveRequirement(index, -1)}
                            disabled={index === 0}
                            size="small"
                          >
                            <KeyboardArrowUpRoundedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('template.creator_move_question_down', 'Move this step down')} arrow>
                        <span>
                          <IconButton
                            aria-label={t('template.creator_move_question_down', 'Move this step down')}
                            onClick={() => handleMoveRequirement(index, 1)}
                            disabled={index === requirements.length - 1}
                            size="small"
                          >
                            <KeyboardArrowDownRoundedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                    <Tooltip title={t('template.creator_edit_question', 'Edit this instruction')} arrow>
                      <IconButton aria-label={t('template.creator_edit_question', 'Edit this instruction')} onClick={() => handleEditRequirement(requirement)} size="small">
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('template.creator_remove_question', 'Remove this instruction')} arrow>
                      <IconButton aria-label={t('template.creator_remove_question', 'Remove this instruction')} onClick={() => {
                        setRequirements((current) => current.filter((item) => item.id !== requirement.id));
                        if (editingRequirementId === requirement.id) resetRequirementDraft();
                      }} size="small" color="default">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Box sx={{ p: 2, border: '1px dashed rgba(87,87,176,.3)', borderRadius: 2, textAlign: 'center', bgcolor: 'rgba(87,87,176,.025)' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t('template.creator_empty_title', 'No extra steps yet')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>{t('template.creator_empty_help', 'That’s okay. Add a step only when someone needs extra guidance beyond the project’s normal settings.')}</Typography>
            </Box>
          )}

          <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <AddCircleOutlineRoundedIcon color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 750, flex: 1 }}>
                {editingRequirementId
                  ? t('template.creator_edit_heading', 'Edit this question or instruction')
                  : t('template.creator_add_heading', 'Add a question or instruction')}
              </Typography>
              <HelpTip title={t('template.creator_add_heading_help', 'Add one question per field to fill in. You can add several different fields from the same project item. Use an instruction without a field for things like connecting an account.')} />
            </Box>
            <Stack spacing={1.5}>
              <Autocomplete
                fullWidth
                options={setupNodeOptions}
                value={selectedNode || null}
                getOptionLabel={(node) => node?.label || ''}
                isOptionEqualToValue={(option, value) => option.ref === value.ref}
                groupBy={(node) => getSetupNodeKindLabel(node.kind, t)}
                onChange={(_event, node) => {
                  setSelectedNodeRef(node?.ref || '');
                  setSelectedFieldPath('');
                  setConnectionStep(Boolean(node?.authentication?.required));
                }}
                disabled={setupNodesLoading || setupNodeOptions.length === 0}
                noOptionsText={t('template.creator_nodes_no_match', 'No project item matches that search.')}
                clearText={t('template.creator_clear_selection', 'Clear selection')}
                openText={t('template.creator_show_choices', 'Show project items')}
                renderOption={(optionProps, node) => (
                  <Box
                    component="li"
                    {...optionProps}
                    key={node.ref}
                    title={getSetupNodeKindHelp(node.kind, t)}
                    sx={{ whiteSpace: 'normal', py: 1 }}
                  >
                    <Tooltip title={getSetupNodeKindHelp(node.kind, t)} arrow placement="right" enterDelay={350}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        {node.kind === 'input' ? <StorageRoundedIcon fontSize="small" color="primary" /> : node.kind === 'flow' ? <AccountTreeRoundedIcon fontSize="small" color="primary" /> : <BoltRoundedIcon fontSize="small" color="primary" />}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>{node.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{getSetupNodeKindLabel(node.kind, t)}</Typography>
                        </Box>
                      </Box>
                    </Tooltip>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('template.creator_node', 'Where should this step appear?')}
                    placeholder={t('template.creator_node_placeholder', 'Type to search project items…')}
                    helperText={t('template.creator_node_help', 'Choose the data source or project step the user will need to work on. Hover over an option for details.')}
                  />
                )}
              />

              {selectedNode && (
                <Box sx={{ p: 1.5, border: '1px solid rgba(87,87,176,.18)', borderRadius: 2, bgcolor: 'rgba(87,87,176,.025)' }}>
                  <FormControlLabel
                    sx={{ mb: 0.8, alignItems: 'flex-start' }}
                    control={(
                      <Switch
                        checked={connectionStep}
                        onChange={(event) => {
                          setConnectionStep(event.target.checked);
                          if (event.target.checked) setSelectedFieldPath('');
                        }}
                        size="small"
                      />
                    )}
                    label={(
                      <Box sx={{ pt: 0.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {t('template.creator_connection_step', 'This step is for connecting an account or service')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('template.creator_connection_step_help', 'The guide will open the project item’s own sign-in or connection flow. Credentials are not entered in the template guide.')}
                        </Typography>
                      </Box>
                    )}
                  />
                  {!connectionStep && <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.8 }}>
                    {t('template.creator_field_question_heading', 'What should the user fill in?')}
                  </Typography>}
                  {!connectionStep && (selectedNode.fields || []).length > 0 ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label={t('template.creator_field_select_label', 'Choose one field for this question')}
                      value={selectedFieldPath}
                      error={duplicateFieldRequirement}
                      renderValue={(fieldPath) => fieldPath
                        ? getFriendlySetupFieldLabel(selectedField || fieldPath, t, selectedNode)
                        : t('template.creator_no_field', 'No specific field — instruction only')}
                      onChange={(event) => {
                        const nextFieldPath = event.target.value;
                        if (nextFieldPath && selectedNode.authentication?.accountField === nextFieldPath) {
                          setConnectionStep(true);
                          setSelectedFieldPath('');
                        } else {
                          setSelectedFieldPath(nextFieldPath);
                        }
                      }}
                      helperText={duplicateFieldRequirement
                        ? t('template.creator_duplicate_field', 'A question already exists for this field. Edit that question instead of adding another one.')
                        : t('template.creator_field_select_help', 'Choose one field to show it directly in the user’s guide. For connecting an account or general guidance, choose “No specific field”.')}
                    >
                      <MenuItem value="">{t('template.creator_no_field', 'No specific field — instruction only')}</MenuItem>
                      {(selectedNode.fields || []).map((field) => {
                        const fieldPath = getSetupFieldPath(field);
                        const rawFieldHelp = field?.helperText || field?.description;
                        const fieldHelp = rawFieldHelp
                          ? t(rawFieldHelp, { defaultValue: rawFieldHelp })
                          : t('template.creator_field_option_help', 'This is a setting on the selected project step. Its current value is never copied into the template.');
                        return (
                          <MenuItem key={fieldPath} value={fieldPath} title={fieldHelp}>
                            <Tooltip title={fieldHelp} arrow placement="right">
                              <Box sx={{ width: '100%' }}>
                                <Typography variant="body2">{getFriendlySetupFieldLabel(field, t, selectedNode)}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {field?.inline === true
                                    ? t('template.creator_field_inline', 'Shown as a field in the setup guide')
                                    : field?.inline === false
                                      ? t('template.creator_field_opens_settings', 'Filled in the project item’s settings')
                                      : t('template.creator_field_maybe_inline', 'Shown here when supported; otherwise in the item’s settings')}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  ) : !connectionStep ? (
                    <Alert severity="info" variant="outlined" sx={{ py: 0.1 }}>
                      {t('template.creator_no_fields_help', 'This project item has no fillable fields in the guide. Add a simple instruction, such as connecting an account or opening its settings.')}
                    </Alert>
                  ) : (
                    <Alert severity="info" variant="outlined" sx={{ py: 0.1 }}>
                      {t('template.creator_connection_preview_help', 'The user will connect this item in its own settings. The guide will not ask for an email, password, token, or API key.')}
                    </Alert>
                  )}
                </Box>
              )}

              <TextField
                fullWidth
                required
                label={connectionStep
                  ? t('template.creator_instruction_question', 'Instruction for the user')
                  : selectedFieldPath && selectedField?.inline === false
                    ? t('template.creator_field_editor_question', 'Instruction for this setting')
                    : selectedFieldPath
                      ? t('template.creator_field_question', 'Question to show above this field')
                      : t('template.creator_instruction_question', 'Instruction for the user')}
                value={requirementQuestion}
                onChange={(event) => { setRequirementQuestion(event.target.value); setRequirementQuestionAttempted(false); }}
                error={requirementQuestionAttempted && !requirementQuestion.trim()}
                helperText={requirementQuestionAttempted && !requirementQuestion.trim()
                  ? t('template.creator_title_error', 'Write the question or instruction the user will see.')
                  : connectionStep
                    ? t('template.creator_connection_question_help', 'Explain which account or service to connect. The guide will show the connection status and open the item’s sign-in flow.')
                    : selectedFieldPath
                      ? selectedField?.inline === false
                        ? t('template.creator_field_editor_question_help', 'The user will see this instruction and the setting name before opening the project item’s settings.')
                        : t('template.creator_field_question_help', 'This question appears directly above the selected field. Example: “Which folder should we monitor?”')
                      : t('template.creator_instruction_question_help', 'For a general instruction, say what to do. Example: “Connect your Google Drive account”.')}
              />
              <TextField
                fullWidth
                label={t('template.creator_example', 'Example (optional)')}
                value={requirementExample}
                onChange={(event) => setRequirementExample(event.target.value)}
                helperText={selectedFieldPath
                  ? t('template.creator_field_example_help', 'Show an example of an acceptable answer. It appears below the field and is not entered for the user. Never use private information.')
                  : t('template.creator_example_help', 'Show a sample value or a good result. Never enter a real password, key, or private data.')}
              />

                {selectedNode && !connectionStep && (
                selectedField && selectedField.inline === false ? (
                  <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                    {t('template.creator_field_editor_only', 'This setting is completed in the project item’s settings, so it will not appear as an inline field in the guide.')}
                  </Alert>
                ) : selectedField ? (
                  <TemplateSetupFieldPreview
                    field={selectedField}
                    fieldLabel={getFriendlySetupFieldLabel(selectedField, t, selectedNode)}
                    question={requirementQuestion}
                    helpText={requirementHelp}
                    example={requirementExample}
                  />
                ) : null
              )}

              <Accordion
                disableGutters
                variant="outlined"
                expanded={explanationOpen}
                onChange={(_event, expanded) => setExplanationOpen(expanded)}
                sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 650 }}>
                    {t('template.creator_add_explanation', 'Add extra explanation (optional)')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label={t('template.creator_help', 'Extra explanation')}
                    value={requirementHelp}
                    onChange={(event) => setRequirementHelp(event.target.value)}
                    helperText={t('template.creator_help_help', 'Tell them where to find information, what to select, or what to check. Use everyday language.')}
                  />
                </AccordionDetails>
              </Accordion>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                <Paper variant="outlined" sx={{ px: 1.25, py: 0.7, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <FormControlLabel
                      sx={{ m: 0, flex: 1, alignItems: 'flex-start' }}
                      control={<Switch checked={requirementRequired} onChange={(event) => setRequirementRequired(event.target.checked)} size="small" />}
                      label={(
                        <Box sx={{ pt: 0.35 }}>
                          <Typography variant="body2" sx={{ fontWeight: 650 }}>{t('template.creator_required', 'Include in the main checklist')}</Typography>
                          <Typography variant="caption" color="text.secondary">{t('template.creator_required_help', 'This marks the step as important; it does not block the project from running.')}</Typography>
                        </Box>
                      )}
                    />
                    <HelpTip title={t('template.creator_required_help', 'This marks the step as important; it does not block the project from running.')} />
                  </Box>
                </Paper>
                <Paper variant="outlined" sx={{ px: 1.25, py: 0.7, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <FormControlLabel
                      sx={{ m: 0, flex: 1, alignItems: 'flex-start' }}
                      control={<Switch checked={confirmExisting} onChange={(event) => setConfirmExisting(event.target.checked)} size="small" />}
                      label={(
                        <Box sx={{ pt: 0.35 }}>
                          <Typography variant="body2" sx={{ fontWeight: 650 }}>{t('template.creator_confirm', 'Ask them to review an existing setting')}</Typography>
                          <Typography variant="caption" color="text.secondary">{t('template.creator_confirm_help', 'Useful when the project already has a value they should check instead of entering again.')}</Typography>
                        </Box>
                      )}
                    />
                    <HelpTip title={t('template.creator_confirm_help', 'Useful when the project already has a value they should check instead of entering again.')} />
                  </Box>
                </Paper>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={editingRequirementId ? <EditOutlinedIcon /> : <AddCircleOutlineRoundedIcon />}
                  onClick={handleAddRequirement}
                  disabled={!selectedNode || duplicateFieldRequirement}
                >
                  {editingRequirementId
                    ? t('template.creator_update_question', 'Save changes')
                    : selectedFieldPath
                      ? t('template.creator_add_field_question', 'Add this field question')
                      : t('template.creator_add_question', 'Add this instruction')}
                </Button>
                {editingRequirementId && <Button variant="text" onClick={resetRequirementDraft}>{t('template.cancel_edit', 'Cancel')}</Button>}
                {!editingRequirementId && requirementDraftHasContent && <Button variant="text" onClick={resetRequirementDraft}>{t('template.creator_discard_draft', 'Clear this step')}</Button>}
              </Box>
            </Stack>
          </Paper>
          {activeStep === 1 && requirementDraftHasContent && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t('template.creator_draft_notice', 'Finish adding this step or cancel it before continuing to the preview.')}
            </Alert>
          )}
        </Box> : <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
          <Typography variant="body2" color="text.secondary">{t('template.creator_preview_intro', 'This is what the user will see when setting up a project from this template.')}</Typography>
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(87, 87, 176, 0.06)', border: '1px solid rgba(87, 87, 176, 0.18)' }}>
            <Typography variant="h6">{templateName || t('template.untitled_template', 'Untitled template')}</Typography>
            {templateDescription && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{templateDescription}</Typography>}
            {requirements.length === 0 ? <Typography variant="body2">{t('template.creator_no_requirements_preview', 'No extra setup questions. Users will configure the nodes using their standard settings.')}</Typography> : requirements.map((requirement, index) => (
              <Box key={requirement.id} sx={{ mt: 1.5, p: 1.5, bgcolor: 'background.paper', borderRadius: 1.5 }}>
                <Typography variant="caption" color="primary">
                  {requirement.node_label}{` · ${requirement.required
                    ? t('template.creator_important_short', 'Important')
                    : t('template.setup_optional', 'Optional')}`}
                </Typography>
                <Typography variant="subtitle2">{index + 1}. {requirement.question}</Typography>
                {requirement.help_text && (!requirement.field_path || requirement.field_schema?.inline === false || requirement.setup_kind === 'connection') && (
                  <Typography variant="body2" color="text.secondary">{requirement.help_text}</Typography>
                )}
                {requirement.setup_kind === 'connection' ? (
                  <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                    {t('template.creator_connection_preview_help', 'The user will connect this item in its own settings. The guide will not ask for an email, password, token, or API key.')}
                  </Alert>
                ) : requirement.field_path && requirement.field_schema?.inline !== false ? (
                  <TemplateSetupFieldPreview
                    field={requirement.field_schema || { path: requirement.field_path }}
                    fieldLabel={getFriendlySetupFieldLabel(requirement.field_schema || requirement.field_path, t)}
                    question={requirement.question}
                    helpText={requirement.help_text}
                    example={requirement.example}
                  />
                ) : requirement.field_path ? (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {t('template.setup_field_in_node_settings', 'Setting to review')}: {getFriendlySetupFieldLabel(requirement.field_schema || requirement.field_path, t)}
                    </Typography>
                    {requirement.example && <Typography variant="caption" color="text.secondary">{t('template.creator_example_prefix', 'Example:')} {requirement.example}</Typography>}
                  </>
                ) : requirement.example ? (
                  <Typography variant="caption" color="text.secondary">{t('template.creator_example_prefix', 'Example:')} {requirement.example}</Typography>
                ) : null}
                {requirement.confirm_existing && <Typography variant="caption" color="text.secondary" display="block">{t('template.creator_confirm', 'Ask the user to confirm an existing value')}</Typography>}
                {(!requirement.field_path || requirement.field_schema?.inline === false || requirement.setup_kind === 'connection') && (
                  <Button fullWidth size="small" variant="outlined" disabled sx={{ mt: 1, textTransform: 'none' }}>
                    {requirement.setup_kind === 'connection'
                      ? t('template.setup_connect', 'Connect account')
                      : requirement.node_kind === 'input'
                      ? t('template.setup_add_input', 'Add or connect this data source')
                      : requirement.confirm_existing
                        ? t('template.setup_review_node', 'Review this node setting')
                        : t('template.setup_open_node', 'Open node settings')}
                  </Button>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Button size="small" disabled>{t('template.setup_back', 'Back')}</Button>
                  <Button size="small" variant="contained" disabled>
                    {index === requirements.length - 1
                      ? t('template.setup_finish', 'Finish guide')
                      : t('template.setup_continue', 'Continue')}
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>}
      </DialogContent>
      
      <DialogActions sx={{
        p: { xs: 2, sm: 4 },
        pt: 3,
        gap: { xs: 0.75, sm: 2 },
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        background: 'rgba(247, 247, 255, 0.3)',
        borderTop: '1px solid rgba(87, 87, 176, 0.1)',
      }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ 
            color: 'rgba(87, 87, 176, 0.8)',
            borderColor: 'rgba(87, 87, 176, 0.3)',
            borderRadius: '8px',
            px: 3,
            py: 1,
            fontWeight: '500',
            textTransform: 'none',
            '&:hover': { 
              backgroundColor: 'rgba(87, 87, 176, 0.05)',
              borderColor: 'rgba(87, 87, 176, 0.5)',
            }
          }}
        >
          {t('close') || 'Cancel'}
        </Button>
        {activeStep > 0 && <Button onClick={() => setActiveStep((step) => step - 1)} variant="text">
          {t('template.back', 'Back')}
        </Button>}
        {activeStep < 2 ? <Button
          variant="contained"
          disabled={!templateName.trim() || (activeStep === 1 && requirementDraftHasContent)}
          onClick={() => setActiveStep((step) => step + 1)}
          sx={{ backgroundColor: primaryColor, textTransform: 'none', borderRadius: '8px', px: 3 }}
        >
          {t('template.continue', 'Continue')}
        </Button> : <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!templateName.trim()}
          sx={{
            backgroundColor: primaryColor,
            borderRadius: '8px',
            px: 3,
            py: 1,
            fontWeight: '600',
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(87, 87, 176, 0.4)',
            '&:hover': { 
              backgroundColor: hoverColor,
              boxShadow: '0 6px 16px rgba(87, 87, 176, 0.5)',
              transform: 'translateY(-1px)',
            },
            '&:disabled': {
              backgroundColor: 'rgba(87, 87, 176, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {t('template.save_template') || 'Save Template'}
        </Button>}
      </DialogActions>
    </Dialog>
  );
};

export default SaveTemplateDialog;
